import { useEffect, useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import toast from "react-hot-toast";
import {
  AppDialog,
  DetailEmpty,
  DetailField,
  FormField,
  FormRow,
  FormToggle,
} from "../../../components/ui";
import { useTenantScope } from "../../../hooks/useTenantScope";
import {
  MAX_POINT_RULE_VALUE,
  MIN_POINT_RULE_VALUE,
  POINT_CUSTOM_WINDOW_MAX_MONTHS,
  POINT_RULE_ACTION_LABELS,
  POINT_RULE_ICON_OPTIONS,
  POINT_RULE_KIND_LABELS,
  POINT_WINDOW_PERIOD_LABELS,
  PointRule,
  PointRuleActionType,
  PointRuleEditableField,
  PointRuleKind,
  PointWindowPeriod,
  canEditPointRuleField,
  formatPointRuleCondition,
  formatPointRuleThresholdCount,
  formatPointRuleValueCompact,
  formatPointRuleValue,
  isPointRuleActionComingSoon,
  isPointRuleActionWithoutAccrual,
} from "../../../models/pointRule";
import {
  useGetEventSubTypesQuery,
  useGetEventTypesQuery,
} from "../../../store/services/events-types";
import {
  useCreatePointRuleMutation,
  useUpdatePointRuleMutation,
} from "../../../store/services/point-rules";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/functions/api-errors";
import {
  CALENDAR_DISPLAY_DATE_FORMAT,
  CALENDAR_LOCALE,
} from "../../../utils/constants/calendar-constants";
import { POINT_RULE_BLOCK_TITLE_STYLE, PointRulePreview } from "./PointRulePreview";
import { PointStreakFields, PointStreakPreview, usePointStreakEditor } from "./PointStreakFields";
import { useAppPointUnitName } from "./useAppPointUnitName";
import {
  DUPLICATE_POINT_RULE_ERROR,
  PAST_CUSTOM_WINDOW_ACTIVATION_MESSAGE,
  POINT_RULE_WITHOUT_ACCRUAL_MESSAGE,
  PointRuleFormErrors,
  PointRuleFormValues,
  THRESHOLD_COUNT_RANGE,
  buildCreatePointRulePayload,
  buildInitialFormValues,
  canCreatePointRuleForAction,
  dayKeyToLocalDate,
  isPointRuleKindTaken,
  isThresholdScopeDuplicate,
  buildPointRuleChanges,
  buildPointRulePreview,
  describePointRuleDuplicate,
  findDuplicatePointRule,
  getAllowedPointRuleActions,
  getAllowedPointRuleKinds,
  getCustomWindowMaxEndDate,
  getPointRuleFieldApplicability,
  hasPastCustomWindowStart,
  withActiveToggled,
  withActiveValue,
  normalizeText,
  validatePointRuleForm,
} from "./pointRuleForm";

export interface PointRuleFormDialogProps {
  visible: boolean;
  onHide: () => void;
  tenantId: string;
  /** null = alta (solo SUPER_ADMIN). */
  rule: PointRule | null;
  /** Todas las reglas del tenant: regla base de una racha nueva y aviso de duplicados en alta. */
  rules: PointRule[];
  /** Se abrió desde el switch de la tabla para prender una regla cuyo período ya empezó. */
  activating?: boolean;
}

const NO_RETROACTIVE_WARNING =
  "Los cambios no son retroactivos: aplican desde la próxima asistencia. Los puntos ya acreditados no cambian.";
const UPDATE_SUCCESS_TOAST = "Regla actualizada. Los cambios no son retroactivos.";
const CREATE_SUCCESS_TOAST = "Regla creada. Los cambios no son retroactivos.";
const PENALTY_POINTS_HINT =
  "Se aplica al completar la asistencia, solo a inscriptos ausentes, y nunca baja el balance por debajo de 0.";
const BONUS_CAP_HINT =
  "Vacío = sin tope. Ningún evento del tenant puede dar más puntos extra que este número.";
const KIND_HINTS: Record<PointRuleKind, string> = {
  atomic: "Da los puntos cada vez que el usuario hace la acción.",
  threshold_window:
    "Da los puntos una sola vez, cuando el usuario llega a una cantidad de actividades entre dos fechas.",
  streak:
    "Premia las semanas seguidas en las que el usuario asiste al menos a un evento. Se configura toda la racha junta: cada cuántas semanas se cumple un hito y cuánto paga cada uno.",
  entity_amount: "Cada evento define sus puntos extra al crearlo o editarlo, hasta el tope de esta regla.",
};
const PENALTY_KIND_HINT =
  "Descuenta los puntos cada vez que el usuario se inscribe a un evento y no asiste.";
const ACTIVE_HINT = "Solo las reglas activas dan o descuentan puntos.";
const VISIBLE_HINT = "Apagado, la regla da puntos igual pero no aparece en la app.";
const FREE_EVENTS_HINT = "Solo cuentan los eventos sin costo de inscripción.";
const PENALTY_FREE_EVENTS_HINT = "Solo descuenta si el evento no tenía costo de inscripción.";
/** Un campo de solo lectura sin motivo es un callejón sin salida: si no se puede editar, decí por qué. */
const EVENT_SCOPE_HINT = "Solo cuentan los eventos de este tipo. Todos = cualquier evento.";
const PENALTY_EVENT_SCOPE_HINT = "Solo descuenta en eventos de este tipo. Todos = cualquier evento.";
const CUSTOM_WINDOW_HINT =
  "Paga una sola vez por usuario. Solo cuentan las asistencias desde que se crea la regla.";
const ACTIVE_BLOCKED_TOOLTIP = "Cargá los puntos antes de activar la regla";
const WITHOUT_ACCRUAL_TOOLTIP = "Esta acción todavía no otorga puntos";
const SAVE_FALLBACK_MESSAGE = "No se pudo guardar la regla";
const REFERRAL_SHARE_TEXT_HINT =
  'El texto que acompaña al link cuando un usuario invita a un amigo se configura en Configuración de puntos → "Texto de invitación", no acá.';
const DUPLICATE_RULE_FALLBACK_MESSAGE = "Ya existe una regla con esa acción y ese umbral";

const pointsHint = (
  ruleKind: PointRuleKind | null,
  windowPeriod: PointWindowPeriod | null,
  isPenalty: boolean
): string | undefined => {
  if (isPenalty) return PENALTY_POINTS_HINT;
  if (ruleKind === "atomic") return "Se dan cada vez que el usuario hace la acción.";
  if (ruleKind === "threshold_window") {
    if (windowPeriod === "week") return "Se dan una vez por semana a quien llega a la cantidad de actividades.";
    if (windowPeriod === "month") return "Se dan una vez por mes a quien llega a la cantidad de actividades.";
    if (windowPeriod === "custom") {
      return "Se dan una sola vez, cuando el usuario llega a la cantidad de actividades.";
    }
    return "Se dan cuando el usuario llega a la cantidad de actividades.";
  }
  return undefined;
};

const thresholdCountHint = (
  windowPeriod: PointWindowPeriod | null,
  actionType: PointRuleActionType | null
): string => {
  const counted =
    actionType === "suggestion_submitted"
      ? "Sugerencias que tiene que mandar el usuario"
      : "Asistencias que tiene que sumar el usuario";
  if (windowPeriod === "week") return `${counted} en cada semana.`;
  if (windowPeriod === "month") return `${counted} en cada mes.`;
  if (windowPeriod === "custom") return `${counted} entre las dos fechas.`;
  return `${counted} en el período.`;
};

const startOfToday = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const latestDay = (first: Date | null, second: Date | null): Date | undefined => {
  if (first === null) return second ?? undefined;
  if (second === null) return first;
  return first.getTime() >= second.getTime() ? first : second;
};

const enumOptions = <T extends string>(labels: Record<T, string>) =>
  (Object.keys(labels) as T[]).map((value) => ({ label: labels[value], value }));

const ACTION_OPTIONS = enumOptions<PointRuleActionType>(POINT_RULE_ACTION_LABELS);
const KIND_OPTIONS = enumOptions<PointRuleKind>(POINT_RULE_KIND_LABELS);

type PointRuleIconOption = (typeof POINT_RULE_ICON_OPTIONS)[number];

const yesNo = (value: boolean): string => (value ? "Sí" : "No");

const TAKEN_OPTION_REASON = "ya existe";
const COMING_SOON_OPTION_REASON = "Próximamente";

/** Las opciones que no se pueden elegir quedan grisadas y al final, con el motivo entre paréntesis. */
const withUnavailableOptionsLast = <T extends string>(
  options: { label: string; value: T }[],
  unavailableReason: (value: T) => string | null
): { label: string; value: T; disabled?: boolean }[] => [
  ...options.filter((option) => unavailableReason(option.value) === null),
  ...options.flatMap((option) => {
    const reason = unavailableReason(option.value);
    return reason === null
      ? []
      : [{ ...option, label: `${option.label} (${reason})`, disabled: true }];
  }),
];

/**
 * Alta y edición de una regla de Chispas. En edición solo se envían los campos
 * editables que cambiaron (`editable_fields` del backend es la fuente de verdad). Los
 * campos que aplican pero no se pueden editar se muestran como texto, nunca como input
 * deshabilitado.
 */
export const PointRuleFormDialog = ({
  visible,
  onHide,
  tenantId,
  rule,
  rules,
  activating = false,
}: PointRuleFormDialogProps) => {
  const isCreating = rule === null;
  const tenantScope = useTenantScope();
  const isSuperAdmin = tenantScope.role === "SUPER_ADMIN";

  const [values, setValues] = useState<PointRuleFormValues>(() => buildInitialFormValues(rule));
  const [attempted, setAttempted] = useState(false);
  // Cada intento fallido de guardar lleva el scroll al resumen de errores.
  const [failedSaves, setFailedSaves] = useState(0);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (failedSaves === 0) return;
    // scrollIntoView no existe en jsdom.
    errorSummaryRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
    });
  }, [failedSaves]);

  // Cada apertura (o cambio de regla) arranca de los valores guardados. Sin dependencias
  // de más: `rules` y `tenantId` no reinician lo que el usuario está escribiendo.
  useEffect(() => {
    if (!visible) return;
    const initialValues = buildInitialFormValues(rule);
    setValues(activating ? withActiveToggled(initialValues, true) : initialValues);
    setAttempted(false);
  }, [visible, rule, activating]);

  const { data: eventTypesData } = useGetEventTypesQuery({ tenantId }, { skip: !visible });
  const { data: eventSubTypesData } = useGetEventSubTypesQuery({ tenantId }, { skip: !visible });
  const [updatePointRule, updateState] = useUpdatePointRuleMutation();
  const [createPointRule, createState] = useCreatePointRuleMutation();

  const eventTypes = eventTypesData ?? [];
  const eventSubTypes = eventSubTypesData ?? [];

  const canEdit = (field: PointRuleEditableField): boolean =>
    rule === null || canEditPointRuleField(rule, field);

  const setField = <K extends keyof PointRuleFormValues>(field: K, value: PointRuleFormValues[K]) =>
    setValues((previous) => ({ ...previous, [field]: value }));

  // Cambiar la acción deja solo los tipos que admite: si hay uno solo se elige solo, y un tipo
  // que ya no aplica se descarta (la base lo rechazaría).
  const setActionType = (actionType: PointRuleActionType | null) =>
    setValues((previous) => {
      const allowedKinds = getAllowedPointRuleKinds(actionType).filter(
        (kind) => actionType === null || !isPointRuleKindTaken(actionType, kind, rules)
      );
      const ruleKind =
        allowedKinds.length === 1
          ? allowedKinds[0]
          : previous.ruleKind !== null && allowedKinds.includes(previous.ruleKind)
          ? previous.ruleKind
          : null;
      return { ...previous, actionType, ruleKind };
    });

  // Elegir el tipo antes que la acción deja solo las acciones que lo admiten: si queda una sola
  // que todavía se puede crear, se elige sola.
  const setRuleKind = (ruleKind: PointRuleKind | null) =>
    setValues((previous) => {
      if (ruleKind === null || previous.actionType !== null) return { ...previous, ruleKind };
      const creatableActions = getAllowedPointRuleActions(ruleKind).filter(
        (actionType) => !isPointRuleKindTaken(actionType, ruleKind, rules)
      );
      return {
        ...previous,
        ruleKind,
        actionType: creatableActions.length === 1 ? creatableActions[0] : null,
      };
    });

  // El subtipo pertenece a un tipo: cambiar el tipo descarta el subtipo elegido.
  const setEventTypeId = (eventTypeId: number | null) =>
    setValues((previous) =>
      previous.eventTypeId === eventTypeId
        ? previous
        : { ...previous, eventTypeId, eventSubTypeId: null }
    );

  const applies = getPointRuleFieldApplicability(
    values.ruleKind,
    values.actionType,
    values.windowPeriod
  );
  const isPenalty = rule ? rule.isPenalty : values.actionType === "no_show";
  // El texto de invitación no es un campo de la regla: vive en Configuración de puntos. Se avisa
  // acá para que quien edita la regla de referidos sepa dónde buscarlo.
  const isReferralRule = (rule ? rule.actionType : values.actionType) === "referral";

  const windowDatesEditable = canEdit("windowStartsOn") && canEdit("windowEndsOn");

  const validationErrors = validatePointRuleForm(values, {
    creating: isCreating,
    canEdit,
    existingRules: rules,
    currentRuleId: rule?.id,
    originalActive: rule?.active,
    originalWindowEndsOn: rule ? dayKeyToLocalDate(rule.windowEndsOn) : undefined,
    originalWindowStartsOn: rule ? dayKeyToLocalDate(rule.windowStartsOn) : undefined,
  });
  // El choque con otra regla se avisa apenas se elige la combinación, sin esperar a Guardar:
  // no es un dato que falte sino algo que la base no va a aceptar.
  const duplicateRule = findDuplicatePointRule(values, rules, rule?.id);
  const duplicateMessage =
    duplicateRule !== null ? describePointRuleDuplicate(duplicateRule, values, rule ?? undefined) : null;
  // El choque entre campañas se resuelve eligiendo otro tipo de evento: se explica ahí, no arriba.
  const isCampaignScopeDuplicate =
    duplicateRule !== null && isThresholdScopeDuplicate(duplicateRule, values, rule ?? undefined);
  // Resumen junto a los botones: el modal tiene scroll y el error de un campo puede quedar
  // fuera de la vista, y entonces parecía que Crear regla no hacía nada.
  const errorSummary = attempted ? Array.from(new Set(Object.values(validationErrors))) : [];
  const shownErrors: PointRuleFormErrors = attempted ? validationErrors : {};
  const hasErrors = Object.keys(validationErrors).length > 0;

  // Sin puntos no se puede prender la regla. Si ya está prendida se deja apagar, y el
  // error de puntos sale al guardar.
  const pointsMissing = applies.pointsValue && values.pointsValue === null;
  const withoutAccrual =
    values.actionType !== null && isPointRuleActionWithoutAccrual(values.actionType);
  const activationBlockedByAccrual = withoutAccrual && rule?.active !== true;
  // Con las fechas editables, prenderla mueve "Desde" a hoy (withActiveToggled); si no se pueden
  // editar, no hay forma de activarla.
  const customWindowStarted = hasPastCustomWindowStart(values) && !windowDatesEditable;
  const activeBlocked =
    canEdit("active") &&
    !values.active &&
    (pointsMissing || activationBlockedByAccrual || customWindowStarted);
  const activeBlockedTooltip = activationBlockedByAccrual
    ? WITHOUT_ACCRUAL_TOOLTIP
    : customWindowStarted
      ? PAST_CUSTOM_WINDOW_ACTIVATION_MESSAGE
      : ACTIVE_BLOCKED_TOOLTIP;

  // El período y la cantidad son todo editable o todo de solo lectura: son los dos campos que
  // arma la misma fila. "Solo actividades gratuitas" YA NO entra acá: se muestra por su cuenta,
  // porque atarlo a los otros dos reimplementaba la matriz de permisos. Si `editable_fields`
  // trae uno sin el otro, atados se esconde un campo que el backend deja editar, o se congela
  // la cantidad por un permiso que no es suyo.
  const thresholdEditable = canEdit("windowPeriod") && canEdit("thresholdCount");
  const hasStructuralFields = applies.threshold || applies.eventScope || applies.freeEventsOnly;
  // El campo se ve SIEMPRE que aplica: editable si se puede, de solo lectura si no. El fallback
  // de solo lectura pedía además `!applies.threshold`, así que en una regla de umbral sin permiso
  // sobre el campo no se renderizaba de ninguna forma y nada explicaba por qué.
  const showFreeToggle = applies.freeEventsOnly && canEdit("freeEventsOnly");
  const showFreeReadOnly = applies.freeEventsOnly && !canEdit("freeEventsOnly");

  const changes = rule ? buildPointRuleChanges(rule, values) : null;
  const primaryDisabled = changes !== null && Object.keys(changes).length === 0;

  // Cada selector ofrece solo lo compatible con el otro. Lo que ya existe se ve grisado y al
  // final, para que lo que se puede elegir quede arriba.
  const allowedKinds = getAllowedPointRuleKinds(values.actionType);
  const isKindCreatable = (kind: PointRuleKind): boolean =>
    values.actionType !== null
      ? !isPointRuleKindTaken(values.actionType, kind, rules)
      : getAllowedPointRuleActions(kind).some(
          (actionType) => !isPointRuleKindTaken(actionType, kind, rules)
        );
  // Un tipo que solo admiten acciones "Próximamente" (la racha) tampoco se puede elegir.
  const isKindComingSoon = (kind: PointRuleKind): boolean =>
    getAllowedPointRuleActions(kind).every(isPointRuleActionComingSoon);
  const kindOptions = withUnavailableOptionsLast(
    KIND_OPTIONS.filter((option) => allowedKinds.includes(option.value)),
    (kind) =>
      isKindComingSoon(kind)
        ? COMING_SOON_OPTION_REASON
        : isKindCreatable(kind)
          ? null
          : TAKEN_OPTION_REASON
  );
  const allowedActions = getAllowedPointRuleActions(values.ruleKind);
  const isActionCreatable = (actionType: PointRuleActionType): boolean =>
    values.ruleKind === null
      ? canCreatePointRuleForAction(actionType, rules)
      : !isPointRuleKindTaken(actionType, values.ruleKind, rules);
  const actionOptions = withUnavailableOptionsLast(
    ACTION_OPTIONS.filter((option) => allowedActions.includes(option.value)),
    (actionType) =>
      isPointRuleActionComingSoon(actionType)
        ? COMING_SOON_OPTION_REASON
        : isActionCreatable(actionType)
          ? null
          : TAKEN_OPTION_REASON
  );

  // Campañas entre fechas de la misma acción: hay una sola por tipo y subtipo, así que los tipos y
  // subtipos que ya tienen una se ven grisados.
  const sameActionCampaigns =
    values.ruleKind === "threshold_window" && values.windowPeriod === "custom"
      ? rules.filter(
          (candidate) =>
            candidate.id !== rule?.id &&
            candidate.actionType === values.actionType &&
            candidate.ruleKind === "threshold_window" &&
            candidate.windowPeriod === "custom"
        )
      : [];
  const isEventTypeTaken = (eventTypeId: number | null): boolean =>
    sameActionCampaigns.some(
      (campaign) =>
        eventTypeId === null ||
        campaign.eventTypeId === null ||
        (campaign.eventTypeId === eventTypeId && campaign.eventSubTypeId === null)
    );
  const isEventSubTypeTaken = (eventSubTypeId: number | null): boolean =>
    sameActionCampaigns.some(
      (campaign) =>
        campaign.eventTypeId === null ||
        (campaign.eventTypeId === values.eventTypeId &&
          (eventSubTypeId === null ||
            campaign.eventSubTypeId === null ||
            campaign.eventSubTypeId === eventSubTypeId))
    );
  // "Todos" ocupado no se muestra como elegido: el campo arranca vacío para que se elija uno libre.
  const allEventTypesTaken = values.eventTypeId === null && isEventTypeTaken(null);
  const allEventSubTypesTaken =
    values.eventTypeId !== null && values.eventSubTypeId === null && isEventSubTypeTaken(null);
  // "Todos" solo se ofrece si se puede elegir. Un tipo o subtipo que ya tiene campaña se ve grisado.
  const markTaken = <T,>(option: { label: string; value: T }, taken: boolean) =>
    taken ? { ...option, label: `${option.label} (ya tiene campaña)`, disabled: true } : option;

  const eventTypeOptions = [
    ...(isEventTypeTaken(null) ? [] : [{ label: "Todos", value: null as number | null }]),
    ...eventTypes.map((eventType) => ({
      label: eventType.eventTypeName,
      value: eventType.eventTypeId as number | null,
    })),
  ].map((option) => markTaken(option, isEventTypeTaken(option.value)));
  const eventSubTypeOptions = [
    ...(values.eventTypeId !== null && isEventSubTypeTaken(null)
      ? []
      : [{ label: "Todos", value: null as number | null }]),
    ...eventSubTypes
      .filter((subType) => subType.eventTypeId === values.eventTypeId)
      .map((subType) => ({
        label: subType.eventSubTypeName,
        value: subType.eventSubTypeId as number | null,
      })),
  ].map((option) =>
    values.eventTypeId === null ? option : markTaken(option, isEventSubTypeTaken(option.value))
  );

  const eventTypeName = (eventTypeId: number | null): string => {
    if (eventTypeId === null) return "Todos";
    return (
      eventTypes.find((eventType) => eventType.eventTypeId === eventTypeId)?.eventTypeName ?? "—"
    );
  };

  const eventSubTypeName = (eventSubTypeId: number | null): string => {
    if (eventSubTypeId === null) return "Todos";
    return (
      eventSubTypes.find((subType) => subType.eventSubTypeId === eventSubTypeId)
        ?.eventSubTypeName ?? "—"
    );
  };

  const describeCampaignScope = (campaign: PointRule): string => {
    if (campaign.eventTypeId === null) return "todos los eventos";
    const subType =
      campaign.eventSubTypeId === null ? "" : ` · ${eventSubTypeName(campaign.eventSubTypeId)}`;
    return `${eventTypeName(campaign.eventTypeId)}${subType}`;
  };
  const campaignScopeHint =
    sameActionCampaigns.length === 0
      ? null
      : `Ya hay campañas de esta acción para: ${sameActionCampaigns
          .map((campaign) => `${describeCampaignScope(campaign)} (${campaign.displayTitle ?? "sin título"})`)
          .join(", ")}. Elegí un tipo de evento libre.`;

  const appPointUnitName = useAppPointUnitName(visible);
  // El preview copia la fila "Cómo ganar" de la app: el valor va como número pelado ("+10").
  const previewValueLabel = formatPointRuleValueCompact(
    buildPointRulePreview(values, tenantId, isPenalty)
  );

  // La racha se configura completa (textos, intervalo y todos sus hitos) dentro de este mismo modal.
  const streakMode = isCreating ? values.ruleKind === "streak" : rule?.ruleKind === "streak";
  const streakEditor = usePointStreakEditor(tenantId, visible && streakMode, isSuperAdmin);

  const handleSave = async () => {
    if (streakMode) {
      if (await streakEditor.save()) onHide();
      return;
    }
    setAttempted(true);
    if (hasErrors) {
      setFailedSaves((count) => count + 1);
      return;
    }

    try {
      if (rule) {
        const updateChanges = buildPointRuleChanges(rule, values);
        if (Object.keys(updateChanges).length === 0) return;
        await updatePointRule({
          id: rule.id,
          tenantId: rule.tenantId,
          changes: updateChanges,
        }).unwrap();
        toast.success(UPDATE_SUCCESS_TOAST);
      } else {
        const payload = buildCreatePointRulePayload(values, tenantId);
        if (payload === null) return;
        await createPointRule(payload).unwrap();
        toast.success(CREATE_SUCCESS_TOAST);
      }
      onHide();
    } catch (error) {
      const fallback =
        isCreating && getApiErrorStatus(error) === 409
          ? DUPLICATE_RULE_FALLBACK_MESSAGE
          : SAVE_FALLBACK_MESSAGE;
      toast.error(getApiErrorMessage(error) ?? fallback);
    }
  };

  const header = streakMode
    ? "Racha"
    : rule
      ? `Editar regla: ${POINT_RULE_ACTION_LABELS[rule.actionType]}`
      : "Crear regla";
  const summaryMessages = streakMode ? streakEditor.errorMessages : errorSummary;

  return (
    <AppDialog
      variant="form"
      visible={visible}
      onHide={onHide}
      header={header}
      primaryLabel={streakMode ? "Guardar racha" : rule ? "Guardar cambios" : "Crear regla"}
      onPrimary={handleSave}
      loading={
        streakMode ? streakEditor.saving : rule ? updateState.isLoading : createState.isLoading
      }
      primaryDisabled={
        streakMode
          ? streakEditor.isLoading || streakEditor.loadError !== null || streakEditor.notConfigured
          : primaryDisabled
      }
      className="neutral-input-controls"
      style={{ width: "min(92vw, 880px)" }}
    >
      <div className="grid">
        <div className="col-12 md:col-7">
          {isCreating && (
            <>
              <FormRow>
                <FormField
                  label="Acción"
                  htmlFor="pointRuleAction"
                  required
                  error={shownErrors.actionType}
                >
                  <Dropdown
                    appendTo="self"
                    inputId="pointRuleAction"
                    value={values.actionType}
                    options={actionOptions}
                    optionDisabled="disabled"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Elegí la acción"
                    showClear
                    onChange={(event) => setActionType(event.value ?? null)}
                    className="w-full"
                  />
                </FormField>
                <FormField
                  label="Tipo de regla"
                  htmlFor="pointRuleKind"
                  required
                  error={shownErrors.ruleKind}
                  hint={
                    values.ruleKind === null
                      ? undefined
                      : isPenalty
                        ? PENALTY_KIND_HINT
                        : KIND_HINTS[values.ruleKind]
                  }
                >
                  <Dropdown
                    appendTo="self"
                    inputId="pointRuleKind"
                    value={values.ruleKind}
                    options={kindOptions}
                    optionDisabled="disabled"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Elegí el tipo de regla"
                    showClear
                    onChange={(event) => setRuleKind(event.value ?? null)}
                    className="w-full"
                  />
                </FormField>
              </FormRow>
            </>
          )}

          {streakMode ? (
            <PointStreakFields editor={streakEditor} canEditStructure={isSuperAdmin} />
          ) : (
            <>
          {duplicateMessage && !isCampaignScopeDuplicate && (
            <Message severity="warn" text={duplicateMessage} className="w-full mb-3" />
          )}
          {withoutAccrual && (
            <Message severity="info" text={POINT_RULE_WITHOUT_ACCRUAL_MESSAGE} className="w-full mb-3" />
          )}
          {isReferralRule && (
            <Message severity="info" text={REFERRAL_SHARE_TEXT_HINT} className="w-full mb-3" />
          )}

          <FormRow>
            {applies.bonusCap && canEdit("pointsValue") && (
              <FormField
                label="Tope de puntos extra por evento"
                htmlFor="pointRuleBonusCap"
                error={shownErrors.pointsValue}
                hint={BONUS_CAP_HINT}
              >
                <InputNumber
                  inputId="pointRuleBonusCap"
                  placeholder="Sin tope"
                  value={values.pointsValue}
                  onValueChange={(event) => setField("pointsValue", event.value ?? null)}
                  useGrouping={false}
                  maxFractionDigits={0}
                  className="w-full"
                />
              </FormField>
            )}
            {applies.bonusCap && !canEdit("pointsValue") && (
              <DetailField label="Tope de puntos extra por evento">
                {rule?.pointsValue ?? <DetailEmpty>Sin tope</DetailEmpty>}
              </DetailField>
            )}
            {applies.pointsValue && canEdit("pointsValue") && (
              <FormField
                label={isPenalty ? "Puntos que se descuentan" : "Puntos"}
                htmlFor="pointRulePoints"
                required={isCreating}
                error={shownErrors.pointsValue}
                hint={pointsHint(values.ruleKind, values.windowPeriod, isPenalty)}
              >
                <InputNumber
                  inputId="pointRulePoints"
                  placeholder="Ej: 10"
                  value={values.pointsValue}
                  onValueChange={(event) => setField("pointsValue", event.value ?? null)}
                  min={MIN_POINT_RULE_VALUE}
                  max={MAX_POINT_RULE_VALUE}
                  useGrouping={false}
                  maxFractionDigits={0}
                  className="w-full"
                />
              </FormField>
            )}
            {applies.pointsValue && !canEdit("pointsValue") && (
              <DetailField label={isPenalty ? "Puntos que se descuentan" : "Puntos"}>
                {rule?.pointsValue ?? <DetailEmpty>Sin configurar</DetailEmpty>}
              </DetailField>
            )}
          </FormRow>

          <FormRow>
            {canEdit("active") ? (
              <FormToggle label="Activa" htmlFor="pointRuleActive" hint={ACTIVE_HINT}>
                {/* inline-flex: un span inline suma el alto del renglón y el label "Activa"
                    quedaba más abajo que "Visible en la app". */}
                <span
                  className="point-rule-active-toggle inline-flex"
                  data-testid="point-rule-active-toggle"
                  data-pr-tooltip={activeBlocked ? activeBlockedTooltip : undefined}
                  data-pr-position="top"
                >
                  {activeBlocked && <Tooltip target=".point-rule-active-toggle" />}
                  <InputSwitch
                    inputId="pointRuleActive"
                    checked={values.active}
                    disabled={activeBlocked}
                    onChange={(event) =>
                      setValues((previous) =>
                        canEdit("windowStartsOn") && canEdit("windowEndsOn")
                          ? withActiveToggled(previous, event.value)
                          : withActiveValue(previous, event.value)
                      )
                    }
                  />
                </span>
              </FormToggle>
            ) : (
              <DetailField label="Activa">{yesNo(values.active)}</DetailField>
            )}
            {canEdit("visibleToUser") ? (
              <FormToggle label="Visible en la app" htmlFor="pointRuleVisible" hint={VISIBLE_HINT}>
                <InputSwitch
                  inputId="pointRuleVisible"
                  checked={values.visibleToUser}
                  onChange={(event) => setField("visibleToUser", event.value)}
                />
              </FormToggle>
            ) : (
              <DetailField label="Visible en la app">{yesNo(values.visibleToUser)}</DetailField>
            )}
          </FormRow>

          <FormRow>
            {canEdit("displayTitle") ? (
              <FormField
                label="Título en la app"
                htmlFor="pointRuleDisplayTitle"
                required={values.visibleToUser}
                error={shownErrors.displayTitle}
              >
                <InputText
                  id="pointRuleDisplayTitle"
                  placeholder="Ej: Asistí a un evento"
                  value={values.displayTitle}
                  maxLength={200}
                  onChange={(event) => setField("displayTitle", event.target.value)}
                  className="w-full"
                />
              </FormField>
            ) : (
              <DetailField label="Título en la app">
                {rule?.displayTitle ?? <DetailEmpty />}
              </DetailField>
            )}
          </FormRow>

          <FormRow>
            {canEdit("displayDescription") ? (
              <FormField label="Descripción en la app" htmlFor="pointRuleDisplayDescription">
                <InputTextarea
                  id="pointRuleDisplayDescription"
                  placeholder="Ej: Sumás puntos cada vez que vas a un evento"
                  value={values.displayDescription}
                  maxLength={200}
                  rows={3}
                  onChange={(event) => setField("displayDescription", event.target.value)}
                  className="w-full"
                />
              </FormField>
            ) : (
              <DetailField label="Descripción en la app">
                {rule?.displayDescription ?? <DetailEmpty />}
              </DetailField>
            )}
          </FormRow>

          <FormRow>
            {canEdit("iconKey") ? (
              <FormField label="Ícono" htmlFor="pointRuleIcon">
                <Dropdown
                  appendTo="self"
                  inputId="pointRuleIcon"
                  value={values.iconKey}
                  options={POINT_RULE_ICON_OPTIONS}
                  optionLabel="label"
                  optionValue="key"
                  placeholder="Elegí un ícono"
                  showClear
                  onChange={(event) => setField("iconKey", event.value ?? null)}
                  itemTemplate={(option: PointRuleIconOption) => (
                    <span className="flex align-items-center gap-2">
                      <i className={option.icon} />
                      {option.label}
                    </span>
                  )}
                  valueTemplate={(option: PointRuleIconOption | null, props) =>
                    option ? (
                      <span className="flex align-items-center gap-2">
                        <i className={option.icon} />
                        {option.label}
                      </span>
                    ) : (
                      <span>{props.placeholder}</span>
                    )
                  }
                  className="w-full"
                />
              </FormField>
            ) : (
              <DetailField label="Ícono">
                {POINT_RULE_ICON_OPTIONS.find((option) => option.key === rule?.iconKey)?.label ?? (
                  <DetailEmpty />
                )}
              </DetailField>
            )}
          </FormRow>

          {hasStructuralFields && (
            <>
              {/* §5.1 — separación de secciones de formulario: 16px. */}
              <h3
                style={{
                  ...POINT_RULE_BLOCK_TITLE_STYLE,
                  margin: "var(--space-16) 0",
                }}
              >
                Cómo se cuenta
              </h3>

              {applies.threshold && thresholdEditable && (
                <FormRow>
                  {/* Las reglas nuevas son siempre entre dos fechas: el período solo se muestra en
                      las semanales o mensuales que ya existían, y no se puede cambiar. */}
                  {values.windowPeriod !== null && values.windowPeriod !== "custom" && (
                    <DetailField label="Período">
                      {POINT_WINDOW_PERIOD_LABELS[values.windowPeriod]}
                    </DetailField>
                  )}
                  <FormField
                    label="Cantidad de actividades"
                    htmlFor="pointRuleThresholdCount"
                    required
                    error={shownErrors.thresholdCount}
                    hint={thresholdCountHint(values.windowPeriod, values.actionType)}
                  >
                    <InputNumber
                      inputId="pointRuleThresholdCount"
                      placeholder="Ej: 3"
                      value={values.thresholdCount}
                      onValueChange={(event) => setField("thresholdCount", event.value ?? null)}
                      min={THRESHOLD_COUNT_RANGE.min}
                      max={THRESHOLD_COUNT_RANGE.max}
                      useGrouping={false}
                      maxFractionDigits={0}
                      className="w-full"
                    />
                  </FormField>
                </FormRow>
              )}
              {applies.customWindow && windowDatesEditable && (
                <FormRow>
                  <FormField
                    label="Desde"
                    htmlFor="pointRuleWindowStartsOn"
                    required
                    hint={CUSTOM_WINDOW_HINT}
                    error={shownErrors.windowStartsOn}
                  >
                    <Calendar
                      appendTo="self"
                      inputId="pointRuleWindowStartsOn"
                      value={values.windowStartsOn}
                      onChange={(event) =>
                        setField("windowStartsOn", event.value instanceof Date ? event.value : null)
                      }
                      dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
                      locale={CALENDAR_LOCALE}
                      placeholder="Elegí la fecha"
                      minDate={startOfToday()}
                      showIcon
                      readOnlyInput
                      className="w-full"
                      inputClassName="w-full"
                    />
                  </FormField>
                  <FormField
                    label="Hasta"
                    htmlFor="pointRuleWindowEndsOn"
                    required
                    hint={`Hasta ${POINT_CUSTOM_WINDOW_MAX_MONTHS} meses después de la fecha desde.`}
                    error={shownErrors.windowEndsOn}
                  >
                    <Calendar
                      appendTo="self"
                      inputId="pointRuleWindowEndsOn"
                      value={values.windowEndsOn}
                      onChange={(event) =>
                        setField("windowEndsOn", event.value instanceof Date ? event.value : null)
                      }
                      dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
                      locale={CALENDAR_LOCALE}
                      placeholder="Elegí la fecha"
                      minDate={latestDay(values.windowStartsOn, isCreating ? startOfToday() : null)}
                      maxDate={
                        values.windowStartsOn
                          ? getCustomWindowMaxEndDate(values.windowStartsOn)
                          : undefined
                      }
                      showIcon
                      readOnlyInput
                      className="w-full"
                      inputClassName="w-full"
                    />
                  </FormField>
                </FormRow>
              )}
              {applies.threshold && !thresholdEditable && rule && (
                <FormRow>
                  <DetailField label="Umbral">{formatPointRuleCondition(rule)}</DetailField>
                </FormRow>
              )}


              {showFreeToggle && (
                <FormRow>
                  <FormToggle
                    label="Solo actividades gratuitas"
                    htmlFor="pointRuleFreeEventsOnly"
                    hint={isPenalty ? PENALTY_FREE_EVENTS_HINT : FREE_EVENTS_HINT}
                  >
                    <InputSwitch
                      inputId="pointRuleFreeEventsOnly"
                      checked={values.freeEventsOnly}
                      onChange={(event) => setField("freeEventsOnly", event.value)}
                    />
                  </FormToggle>
                </FormRow>
              )}
              {showFreeReadOnly && (
                <FormRow>
                  <DetailField label="Solo actividades gratuitas">
                    {yesNo(values.freeEventsOnly)}
                  </DetailField>
                </FormRow>
              )}

              {applies.eventScope && (
                <FormRow>
                  {canEdit("eventTypeId") ? (
                    <FormField
                      label="Tipo de evento"
                      htmlFor="pointRuleEventType"
                      error={shownErrors.eventTypeId}
                      hint={campaignScopeHint ?? (isPenalty ? PENALTY_EVENT_SCOPE_HINT : EVENT_SCOPE_HINT)}
                    >
                      <Dropdown
                        appendTo="self"
                        inputId="pointRuleEventType"
                        value={allEventTypesTaken ? undefined : values.eventTypeId}
                        options={eventTypeOptions}
                        optionDisabled="disabled"
                        optionLabel="label"
                        optionValue="value"
                        placeholder={isEventTypeTaken(null) ? "Elegí un tipo de evento" : "Todos"}
                        onChange={(event) => setEventTypeId(event.value)}
                        className="w-full"
                      />
                    </FormField>
                  ) : (
                    <DetailField label="Tipo de evento">
                      {eventTypeName(rule?.eventTypeId ?? null)}
                    </DetailField>
                  )}
                  {canEdit("eventSubTypeId") ? (
                    <FormField label="Subtipo de evento" htmlFor="pointRuleEventSubType">
                      <Dropdown
                        appendTo="self"
                        inputId="pointRuleEventSubType"
                        value={allEventTypesTaken || allEventSubTypesTaken ? undefined : values.eventSubTypeId}
                        options={eventSubTypeOptions}
                        optionDisabled="disabled"
                        optionLabel="label"
                        optionValue="value"
                        placeholder={
                          values.eventTypeId === null
                            ? "Primero elegí un tipo"
                            : isEventSubTypeTaken(null)
                              ? "Elegí un subtipo"
                              : "Todos"
                        }
                        disabled={values.eventTypeId === null}
                        onChange={(event) => setField("eventSubTypeId", event.value)}
                        className="w-full"
                      />
                    </FormField>
                  ) : (
                    <DetailField label="Subtipo de evento">
                      {eventSubTypeName(rule?.eventSubTypeId ?? null)}
                    </DetailField>
                  )}
                </FormRow>
              )}
            </>
          )}
            </>
          )}
        </div>

        <div className="col-12 md:col-5">
          {streakMode ? (
            <PointStreakPreview form={streakEditor.form} unitName={appPointUnitName} />
          ) : (
            <PointRulePreview
              actionType={values.actionType ?? "checkin"}
              iconKey={values.iconKey}
              title={normalizeText(values.displayTitle)}
              description={normalizeText(values.displayDescription)}
              valueLabel={previewValueLabel}
              isPenalty={isPenalty}
              visibleToUser={values.visibleToUser}
            />
          )}
        </div>
      </div>

      <Message severity="warn" text={NO_RETROACTIVE_WARNING} className="w-full mt-3" />

      {summaryMessages.length > 0 && (
        <div ref={errorSummaryRef}>
          <Message
            severity="error"
            className="w-full mt-3 justify-content-start"
            content={
              <div>
                <div className="font-semibold mb-1">
                  {rule ? "No se puede guardar todavía:" : "No se puede crear la regla todavía:"}
                </div>
                <ul className="m-0 pl-3">
                  {summaryMessages.map((message) => (
                    <li key={message}>
                      {message === DUPLICATE_POINT_RULE_ERROR && duplicateMessage
                        ? duplicateMessage
                        : message}
                    </li>
                  ))}
                </ul>
              </div>
            }
          />
        </div>
      )}
    </AppDialog>
  );
};
